/* eslint-disable react/no-unknown-property */
import 'reflect-metadata';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/tiptap/styles.css';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import 'flexboxgrid/css/flexboxgrid.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { BaseProvider } from 'baseui';
import { PLACEMENT, ToasterContainer } from 'baseui/toast';
import { styletron } from 'common/config/styletron.config';
import { DefaultLayout } from 'common/layouts';
import { queryClient } from 'common/repositories/query-client';
import stylesColors from 'common/styles/styles.colors';
import { PROJECT_THEME } from 'common/styles/styletron.theme';
import { CredentialCookieStorage } from 'components/common/credential-cookie-storage';
import { getLanguage } from 'components/common/language-storage';
import PrefixUrlChanger from 'components/prefix-url-changer';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { EmployeeProvider } from 'hooks/use-employee';
import yupEnValidation from 'locales/en/validation.yup';
import yupIdValidation from 'locales/id/validation.yup';
import merge from 'lodash/merge';
import theme from 'mantine-components/config/theme';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import appWithI18n from 'next-translate/appWithI18n';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { Provider as StyletronProvider } from 'styletron-react';
import { setLocale } from 'yup';

import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import i18nConfig from '../../i18n';

// It is required to extend dayjs with customParseFormat plugin
// in order to parse dates with custom format
dayjs.extend(customParseFormat);

function MyApp({ Component, pageProps }: AppProps) {
  const { t } = useTranslation();
  const { locale, replace, asPath, prefetch, pathname } = useRouter();

  React.useEffect(() => {
    if (locale === 'en') {
      setLocale(yupEnValidation as any);
    } else {
      setLocale(yupIdValidation as any);
    }
  }, [locale]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const language = getLanguage() ?? 'en';
    if (locale === language) return;
    replace(asPath, undefined, {
      locale: language,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    prefetch('/');
    prefetch('/login');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const credential = CredentialCookieStorage.get('access-token');
    const isLoginPage = pathname === '/login';

    if (credential && isLoginPage) {
      replace('/');
    }
    if (!credential && !isLoginPage) {
      replace('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <style global jsx>
        {resetStyle}
      </style>
      <Head>
        <meta name="robots" content="noindex,nofollow" />
        <title>{t('common:title')}</title>
      </Head>
      <MantineProvider theme={theme}>
        <Notifications position="top-right" withinPortal autoClose={3000} />
        <StyletronProvider value={styletron}>
          <ToasterContainer
            overrides={{
              Root: {
                style: {
                  zIndex: 999,
                  wordBreak: 'break-all',
                },
              },
            }}
            autoHideDuration={1500}
            placement={PLACEMENT.topRight}
          />
          <BaseProvider theme={PROJECT_THEME}>
            <QueryClientProvider client={queryClient}>
              <EmployeeProvider>
                {/* <RefreshTokenHandler /> */}
                <PrefixUrlChanger />
                <DefaultLayout>
                  <Component {...pageProps} />
                </DefaultLayout>
              </EmployeeProvider>
            </QueryClientProvider>
          </BaseProvider>
        </StyletronProvider>
      </MantineProvider>
    </>
  );
}

export default appWithI18n(MyApp as any, {
  ...i18nConfig,
  localeDetection: false,
  loadLocaleFrom: (locale, ns) => {
    const englishTexts = import(`../locales/en/${ns}.json`);
    let countrySpecific = Promise.resolve({});
    let languageOnly = Promise.resolve({});
    if (locale) {
      countrySpecific = import(
        `../locales/${locale.replace('-', '_')}/${ns}.json`
      ).catch(() => ({}));
      languageOnly = import(
        `../locales/${locale.substring(0, 2)}/${ns}.json`
      ).catch(() => ({}));
    }
    return Promise.all([englishTexts, countrySpecific, languageOnly]).then(
      ([en, country, language]) => merge({}, en, country, language),
    );
  },
});

const resetStyle = `
:root {
  --white: #FFFFFF;
  --borderOpaque: #E2E2E2;

  --skyDarkest: #707475;
  --skyDark: #8A8E8F;
  --skyDefault: #A4A7A8;
  --skyLight: #BEC0C1;
  --skyLighter: #F2F2F3;
  --skyLightest: #FFFFFF;

  --inkDark: #0C0D0D;
  --inkDefault: #252627;
  --inkLight: #3E4041;
  --inkLighter: #575A5B;
  --inkLightest: #707475;

  --primaryDark: #274468;
  --primaryDefault: #5D7B9F;
  --primaryLight: #799DC9;
  --primaryLighter: #9DB6D3;
  --primaryLightest: #CBDCF0;

  --blueDark: #0077E5;
  --blueDefault: #1890FF;
  --blueLight: #4CA9FF;
  --blueLighter: #80C2FF;
  --blueLightest: #CCE6FF;
}

h1, h2, h3, h4, h5, h6 {
  margin: 0;
}
html {
  font-family: Open Sans, Segoe UI, 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Helvetica Neue, sans-serif;
  font-size: 16px;
  clear: both;
  box-shadow: none;
}
#__next > div:first-of-type {
  width: 100%;
  height: 100%;
}
* {
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: darkgrey transparent;
}
*::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}
*::-webkit-scrollbar-track {
  background: none;
  border-radius: 50%;
}
*::-webkit-scrollbar-thumb {
  background-color: darkgrey;
  border-radius: 24px;
}

.no-scrollbar::-webkit-scrollbar{
  width: 0;
  height: 0;
}

*::before {
  box-sizing: border-box;
}
*::after {
  box-sizing: border-box;
}
ul[class] {
  margin: 0;
  list-style: none;
}
ol[class] {
  margin: 0;
  list-style: none;
}
body {
  max-width: 100vw;
  min-height: 100vh;
  scroll-behavior: smooth;
  text-rendering: optimizeSpeed;
  line-height: initial;
  margin: 0;
  overscroll-behavior-y: none;
  overflow: hidden;
}
body > div#__next {
  height: 100vh;
}
figure {
  margin: 0;
}
blockquote {
  margin: 0;
}
dl {
  margin: 0;
}
dd {
  margin: 0;
}
a:not([class]) {
  text-decoration-skip-ink: auto;
}
img {
  max-width: 100%;
  display: block;
}
input {
  font: inherit;
}
button {
  font: inherit;
}
textarea {
  font: inherit;
}
select {
  font: inherit;
}
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

.RRT__showmore > div {
  box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
}

.RRT__showmore-label {
  padding: 0px 16px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  box-shadow: none !important;
}
.RRT__showmore{
  z-index:15;
}

.RRT__removable {
  position: unset !important;
}

.RRT__removable-text {
  font-size: 14px;
  margin-right: 4px !important;
}

.RRT__removable-icon {
  position: absolute;
  top: 5px !important;
  right: 8px !important;
  color: transparent;
  width: 16px;
  font-size: 19px !important;
  height: 16px;
  border-radius: 10px;
  background : transparent;
}

.RRT__tab--selected {
  border-top-left-radius : 8px;
  border-top-right-radius : 8px;
}

.RRT__tab {
  border-width : 0px;
}

.RRT__container{
}

.RRT__panel {
  border : none;
  padding : 0 !important;
}

.RRT__removable-icon::before {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 18px !important;
  content: "+";
  transform: rotate(45deg);
  position: absolute;
  color: black !important;
  top: 50%;
  left: 50%;
  right: 50%;
  bottom: 50%;
}

.ck-editor__editable {
  min-height: 300px;
}

.rdw-editor-main, .rdw-editor-toolbar {
  border: 1px solid #c4c4c4 !important;
  padding: 8px;
}

.rdw-editor-main {
  min-height: 300px;
}

.before-border::before{
  content : '';
  border-left: 4px solid white;

    
  -webkit-transition: all 200ms ease;
  -moz-transition: all 200ms ease;
  -ms-transition: all 200ms ease;
  -o-transition: all 200ms ease;
  transition:  all 200ms ease-out;
}

.before-border-active::before{
  content : '';
  border-left: 4px solid #5D7B9F;

  
  -webkit-transition: all 200ms ease;
  -moz-transition: all 200ms ease;
  -ms-transition: all 200ms ease;
  -o-transition: all 200ms ease;
  transition:  all 200ms ease-out;
}

.table-component::-webkit-scrollbar-track {
  background-color: ${stylesColors.skyLighter};
  width: 4px;
  border-radius: 0px;
}

.table-component::-webkit-scrollbar {
  width: 4px;
}

.table-component::-webkit-scrollbar-thumb {
  background-color: ${stylesColors.skyLighter};
}

.side-navigation-container::-webkit-scrollbar-thumb{
  background-color : ${stylesColors.primaryLight};
}

.--aggregate {
  background-color: ${stylesColors.bg1};
  font-weight: 600;
}

ul[role=listbox] {
  padding: 0 !important;
}

.portalRef {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}
`;
